/**
 * Boolean expression AST + 3-valued evaluation for K-NAMHS diagnostic criteria.
 *
 * Direct TypeScript port of
 * `MoDN/modn/scripts/experiment/min_questions/boolean.py`. Each DISC-5
 * diagnosis (Appendix A of the technical manual) is a Boolean combination of
 * atomic predicates of the form `observed[feature] in accept`. We port only the
 * structural pieces the on-device engine needs:
 *
 *   - the AST nodes (Atom / Not / And / Or / AtLeastK),
 *   - `evaluate` — partial-assignment evaluation returning true / false / null
 *     (null = indeterminate),
 *   - `isDetermined` — the DISC-5 stopping predicate,
 *   - `atomsIn` / `featuresIn` — collect atoms / feature names,
 *   - `residualSize` — the structural cost used by the data-blind picker.
 *
 * The data-dependent helpers in the Python module (`probability_true`,
 * `compute_atom_marginals`, the Poisson-binomial tail) are intentionally NOT
 * ported: the structural picker does not use them, and they would require the
 * DTA-gated training data.
 *
 * NaN handling mirrors the Python invariant: an atom whose feature is *present
 * but NaN* in `observed` evaluates to `false` (the question was gated out by a
 * non-endorsed screen, so its "Yes"-style condition cannot fire). An atom whose
 * feature is *absent* from `observed` evaluates to `null` (not asked yet).
 */

// A single observed answer: a cleaned category label (string), a number (the
// few continuous onset/year items — never referenced by any criterion), or a
// blocked/NaN marker. We use `null` for NaN; numeric NaN is also tolerated.
export type Answer = string | number | null;

// `Observed` distinguishes "asked but blocked/NaN" (present with null/NaN) from
// "not asked yet" (key absent) — so a Map (with `.has`) is used, not a record.
export type Observed = Map<string, Answer>;

// 3-valued truth: true / false / null (null = indeterminate, == Python None).
export type Tri = boolean | null;

export type Atom = {kind: 'atom'; feature: string; accept: ReadonlySet<string | number>};
export type Not = {kind: 'not'; inner: Expr};
export type And = {kind: 'and'; clauses: Expr[]};
export type Or = {kind: 'or'; clauses: Expr[]};
export type AtLeastK = {kind: 'atleast'; clauses: Expr[]; k: number};

export type Expr = Atom | Not | And | Or | AtLeastK;

// ---------------------------------------------------------------------------
// Constructors (mirror the Python `.of` factories, incl. single-clause collapse)
// ---------------------------------------------------------------------------

export const atom = (feature: string, ...values: Array<string | number>): Atom => ({
  kind: 'atom',
  feature,
  accept: new Set(values),
});

export const not = (inner: Expr): Not => ({kind: 'not', inner});

export const and = (...clauses: Expr[]): Expr =>
  clauses.length === 1 ? clauses[0] : {kind: 'and', clauses};

export const or = (...clauses: Expr[]): Expr =>
  clauses.length === 1 ? clauses[0] : {kind: 'or', clauses};

export const atLeast = (k: number, ...clauses: Expr[]): AtLeastK => ({
  kind: 'atleast',
  clauses,
  k,
});

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

const isBlocked = (v: Answer): boolean =>
  v === null || (typeof v === 'number' && Number.isNaN(v));

const evalAtom = (a: Atom, observed: Observed): Tri => {
  if (!observed.has(a.feature)) {
    return null;
  }
  const v = observed.get(a.feature) as Answer;
  // NaN / null is a definitive "not in accept" — see module docstring.
  if (isBlocked(v)) {
    return false;
  }
  return a.accept.has(v as string | number);
};

export const evaluate = (expr: Expr, observed: Observed): Tri => {
  switch (expr.kind) {
    case 'atom':
      return evalAtom(expr, observed);
    case 'not': {
      const v = evaluate(expr.inner, observed);
      return v === null ? null : !v;
    }
    case 'and': {
      let anyUnknown = false;
      for (const c of expr.clauses) {
        const v = evaluate(c, observed);
        if (v === false) {
          return false;
        }
        if (v === null) {
          anyUnknown = true;
        }
      }
      return anyUnknown ? null : true;
    }
    case 'or': {
      let anyUnknown = false;
      for (const c of expr.clauses) {
        const v = evaluate(c, observed);
        if (v === true) {
          return true;
        }
        if (v === null) {
          anyUnknown = true;
        }
      }
      return anyUnknown ? null : false;
    }
    case 'atleast': {
      let nTrue = 0;
      let nUnknown = 0;
      for (const c of expr.clauses) {
        const v = evaluate(c, observed);
        if (v === true) {
          nTrue += 1;
        } else if (v === null) {
          nUnknown += 1;
        }
      }
      if (nTrue >= expr.k) {
        return true;
      }
      // If even all unknowns going True cannot reach k, it is definitely False.
      if (nTrue + nUnknown < expr.k) {
        return false;
      }
      return null;
    }
    default: {
      // Exhaustiveness guard.
      const _never: never = expr;
      throw new Error(`Unknown expr node: ${JSON.stringify(_never)}`);
    }
  }
};

export const isDetermined = (expr: Expr, observed: Observed): boolean =>
  evaluate(expr, observed) !== null;

// ---------------------------------------------------------------------------
// Atom / feature collection
// ---------------------------------------------------------------------------

const collectAtoms = (expr: Expr, out: Atom[]): void => {
  switch (expr.kind) {
    case 'atom':
      out.push(expr);
      return;
    case 'not':
      collectAtoms(expr.inner, out);
      return;
    case 'and':
    case 'or':
    case 'atleast':
      for (const c of expr.clauses) {
        collectAtoms(c, out);
      }
      return;
    default: {
      const _never: never = expr;
      throw new Error(`Unknown expr node: ${JSON.stringify(_never)}`);
    }
  }
};

export const atomsIn = (expr: Expr): Atom[] => {
  const out: Atom[] = [];
  collectAtoms(expr, out);
  return out;
};

/** Distinct feature names referenced by `expr`, in deterministic (sorted) order. */
export const featuresIn = (expr: Expr): string[] => {
  const seen = new Set<string>();
  for (const a of atomsIn(expr)) {
    seen.add(a.feature);
  }
  return Array.from(seen).sort();
};

// ---------------------------------------------------------------------------
// Residual size — structural cost for the data-blind picker
// ---------------------------------------------------------------------------

/**
 * Number of "unresolved degrees of freedom" left in `expr` under `observed`.
 * Lower is better. A determined expression has residual 0. Mirrors
 * `boolean.residual_size`.
 */
export const residualSize = (expr: Expr, observed: Observed): number => {
  if (evaluate(expr, observed) !== null) {
    return 0;
  }
  switch (expr.kind) {
    case 'atom':
      return 1;
    case 'not':
      return residualSize(expr.inner, observed);
    case 'and':
    case 'or':
      return expr.clauses.reduce((acc, c) => acc + residualSize(c, observed), 0);
    case 'atleast': {
      let nTrue = 0;
      const unknownResiduals: number[] = [];
      for (const c of expr.clauses) {
        const cv = evaluate(c, observed);
        if (cv === true) {
          nTrue += 1;
        } else if (cv === null) {
          unknownResiduals.push(residualSize(c, observed));
        }
        // known-False clauses contribute 0
      }
      const slotsNeeded = Math.max(0, expr.k - nTrue);
      unknownResiduals.sort((x, y) => x - y);
      return unknownResiduals.slice(0, slotsNeeded).reduce((a, b) => a + b, 0);
    }
    default: {
      const _never: never = expr;
      throw new Error(`Unknown expr node: ${JSON.stringify(_never)}`);
    }
  }
};
